/** Shared WS8 bits: write-error display (409 conflict + Reload, 422 every detail), live invalidation, labels. */
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { ApiError } from "../api/client";
import { ErrorBox } from "../components/ErrorBox";
import { StatusPill } from "../components/StatusPill";
import { onLiveEvent, type LiveEvent } from "../lib/live";
import type { StepOut } from "./types";

export function WriteError({ error, onReload, testId = "conflict" }: {
  error: unknown; onReload: () => void; testId?: string;
}) {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 409) {
    const holder = typeof error.body.holder === "string" ? error.body.holder : null;
    return (
      <div role="alert" className="error-box" data-testid={testId}>
        <strong>409 conflict:</strong> {error.message}.{" "}
        {holder ? `${holder} is handling it. ` : "Someone else changed it first. "}
        <button type="button" data-testid={`${testId}-reload`} onClick={onReload}>Reload</button>
      </div>
    );
  }
  return <ErrorBox error={error} />;
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.status}: ${e.message}${e.details.length ? ` (${e.details.join("; ")})` : ""}`;
  return e instanceof Error ? e.message : String(e);
}

/** Invalidate `keys` when a live event matches (throttled), so other actors' work shows up. */
export function useLiveInvalidate(match: (e: LiveEvent) => boolean, keys: QueryKey[]): void {
  const qc = useQueryClient();
  const ref = useRef({ match, keys });
  ref.current = { match, keys };
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = onLiveEvent((e) => {
      if (!ref.current.match(e) || timer) return;
      timer = setTimeout(() => {
        timer = null;
        for (const k of ref.current.keys) void qc.invalidateQueries({ queryKey: k });
      }, 300);
    });
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [qc]);
}

export function Steps({ steps }: { steps: readonly StepOut[] }) {
  if (steps.length === 0) return null;
  return (
    <ul className="ws8-steps" aria-label="Pipeline steps">
      {steps.map((s) => (
        <li key={s.step}>
          <StatusPill state={s.state} label={s.step} title={s.message ?? undefined} />
          {s.message ? <span className="ws8-muted"> {s.message}</span> : null}
        </li>
      ))}
    </ul>
  );
}

export function when(t: number | null | undefined): string {
  if (!t) return "";
  return new Date(t * 1000).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false });
}

export function Loading({ what }: { what: string }) {
  return <p className="ws8-muted" role="status" data-testid="loading">Loading {what}…</p>;
}
